<template>
  <div id="login" class="login-container" v-if="!ltiProcessing">
    <loginSidebar />
    <div class="login-right">
      <div class="login-form" v-if="ssoUser">
        <h3>SSO Login Required</h3>
        <p>{{ ssoMessage }}</p>
      </div>
      <div class="login-form" v-if="!ssoUser">
        <VeeForm ref="form" @submit="login">
          <div class="form-group">
              <h3>Log In to your account</h3>
              <p>Enter your details below</p>
            </div>
            <div class="form-group">
              <label>Email Address</label>
              <VeeField name="username" rules="required" v-model="input.username" v-slot="{ field, errors }">
                <input type="text" v-bind="field" @input="valueChange()" class="form-control" />
                <span
                  :class="[isActive ? 'invalid' : '']"
                  :style="[isActive ? {'display': 'block'} : {'display': 'none'}]"
                  v-if="errors[0]"
                >{{ errors[0].replace("The ", "" )}}</span>
              </VeeField>
            </div>
            <div class="form-group">
              <div class="d-flex">
                <label>Password</label>
                <router-link to="/forgotPassword" class="ml-auto forgot-link" href="javascript:void(0);">Forgot password?</router-link>
              </div>
              <div class="position-relative">
              <VeeField name="password" rules="required" v-model="input.password" v-slot="{ field, errors }">
                 <input
                  :type="passwordFieldType"
                  v-bind="field"
                  class="form-control"
                  @input="valueChange()"
                />
                <span
                  :class="[isActive ? 'invalid' : '']"
                  :style="[isActive ? {'display': 'block'} : {'display': 'none'}]"
                  v-if="errors[0]"
                >{{ errors[0].replace("The ", "" )}}</span>
              </VeeField>
                <a
                  class="forgot-show"
                  @click="switchVisibility"
                  v-if="passwordFieldType === 'password'"
                >Show</a>
                <a
                  class="forgot-show"
                  @click="switchVisibility"
                  v-if="passwordFieldType === 'text'"
                >Hide</a>
                <span class="invalid" v-if="invalidCred">Invalid Credentials</span>
              </div>
            </div>
            <div class="form-group pt-1 text-right">
              <button type="submit" class="btn primary login-btn">Log In</button>
            </div>
        </VeeForm>
      </div>
    </div>
  </div>
</template>
<script>
import api from "../services/api";
import { utilFunctionService } from "@/utils/utils.service";
import loginSidebar from "../components/loginSidebar.vue";
import { commonConfig, isDigits } from "@/utils/commonConfig";

export default {
  name: "Login-component",
  components: {
    loginSidebar
  },
  data() {
    return {
      input: {
        username: "",
        password: ""
      },
      isActive: "",
      invalidCred: false,
      passwordFieldType: "password",
      loading: false,
      ltiProcessing: false,
      ssoUser: false,
      ssoMessage: commonConfig.appMessages.ssoLoginRequired
    };
  },
   created() {
     // Vue Router (history mode) doesn't parse params from the hash fragment.
     // The backend redirects as /#/login?id=...&token=... so fall back to parsing window.location.hash.
     let ltiId = this.$route.query.id;
     let ltiToken = this.$route.query.token !== undefined ? this.$route.query.token : null;
     let hashParams = null;
     if (!ltiId || ltiToken === null) {
       const hash = window.location.hash;
       if (hash.includes('?')) {
         hashParams = new URLSearchParams(hash.slice(hash.indexOf('?')));
         ltiId = ltiId || hashParams.get('id');
         if (ltiToken === null) ltiToken = hashParams.get('token');
       }
     }
     if (ltiId && ltiToken !== null){
        this.handleLtiLaunch(ltiId, ltiToken, hashParams);
        return;
     }
     // Clear any stale SSO flag so it does not interfere while the user is
     // on the login page. Truly SSO-only users will be informed via the
     // 403 response when they attempt manual login.
     utilFunctionService.removeLocalStorageService('sso_authenticated');
  },
  methods: {
    handleLtiLaunch(ltiId, ltiToken, hashParams) {
      this.ltiProcessing = true;
      localStorage.clear();
      this.$store.commit('lti/clearLtiState');

      // Use hashParams as fallback when Vue Router couldn't parse the hash query string.
      // Normalize null (URLSearchParams.get returns null for missing keys) to undefined
      // so optional-param validation checks (=== undefined) work correctly.
      const q = (key) => this.$route.query[key] ?? hashParams?.get(key) ?? undefined;

      const id = ltiId;
      const loId = q('loId');
      const moduleId = q('moduleId');
      const sectionId = q('section_id');
      const contextScope = q('context_scope');
      const ltiReturnUrl = q('lti_return_url');
      const userSectionId = q('user_section_id');
      const messageType = q('message_type');

      const idValid = isDigits(id);
      const loIdValid = loId === undefined || isDigits(loId);
      const moduleIdValid = moduleId === undefined || isDigits(moduleId);
      const sectionIdValid = sectionId === undefined || isDigits(sectionId);
      const userSectionIdValid = userSectionId === undefined || isDigits(userSectionId);
      if (!idValid || !loIdValid || !moduleIdValid || !sectionIdValid || !userSectionIdValid) {
        this.ltiProcessing = false;
        utilFunctionService.showerr(commonConfig.appMessages.invalidDeepLinkParams);
        this.$router.replace({ name: 'login' });
        return;
      }
      let token = ltiToken;
      if (typeof token !== 'string') {
        this.ltiProcessing = false;
        utilFunctionService.showerr(commonConfig.appMessages.invalidAuthToken);
        this.$router.replace({ name: 'login' });
        return;
      }
      token = token.trim();
      const tokenIsValidLength = token.length > 0 && token.length <= 2048;
      const tokenFormatRegex = /^[A-Za-z0-9._~+/=-]+$/;
      const tokenIsValidFormat = tokenFormatRegex.test(token);
      if (!tokenIsValidLength || !tokenIsValidFormat) {
        this.ltiProcessing = false;
        utilFunctionService.showerr(commonConfig.appMessages.invalidAuthToken);
        this.$router.replace({ name: 'login' });
        return;
      }
      const _fb = new FormData();
      _fb.append("user[id]", id);
      _fb.append("user[launch_token]", token);
      api
        .loginLtiUser(_fb)
        .then(res => {
          const result = JSON.stringify(res["data"]);
          utilFunctionService.setLocalStorageService("instructor", result);
          // Mark this as an LTI session so that on next app boot (after tab close),
          // stale auth is cleared before the new LTI launch is processed.
          localStorage.setItem('lti_session', '1');

          if (ltiReturnUrl) {
            try {
              const parsed = new URL(ltiReturnUrl);
              if (parsed.protocol === 'https:' || parsed.protocol === 'http:') {
                utilFunctionService.setLocalStorageService("lti_return_url", JSON.stringify(ltiReturnUrl));
              }
            } catch {
              // invalid URL, do not store
            }
          }

          if (sectionId) {
            this.$store.commit('lti/setSectionId', sectionId);
          }

          if (userSectionId && messageType === 'LtiSubmissionReviewRequest') {
            this.$store.commit('lti/setIsSubmissionReview', true);
            this.$router.replace({
              name: 'learnerFeedback',
              params: { userSectionId: userSectionId }
            });
          } else if (contextScope === 'submission_review' && sectionId) {
            this.$router.replace({
              name: 'Reporting view',
              query: { tab: 'grading', section_id: sectionId }
            });
          } else if (loId) {
            this.$router.replace({
              name: 'learnerCentralLo',
              params: {
                id: userSectionId || id,
                loId,
                ...(moduleId && { moduleId })
              }
            });
          } else {
            this.$router.replace({ name: 'active'});
          }
        })
        .catch(() => {
          this.ltiProcessing = false;
          this.invalidCred = true;
          utilFunctionService.showerr(commonConfig.appMessages.ltiAuthFailed);
        });
    },
    submitForm() {
      this.login();
    },
    valueChange() {
      this.invalidCred = false;
    },
    switchVisibility() {
      this.passwordFieldType =
        this.passwordFieldType === "password" ? "text" : "password";
    },
    login() {
      this.$refs.form.validate().then(({ valid }) => {
        this.isActive = true;
        if (!valid) {
          return;
        } else {
          const user = {
            email: this.input.username,
            password: this.input.password
          };
          this.loading = true;
          api
            .loginUser(user)
            .then(res => {
              utilFunctionService.showSuccess(
                  commonConfig.appMessages.loginSuccess
              );
              utilFunctionService.setLocalStorageService(
                "instructor",
                JSON.stringify(res.data)
              );
              const { name: routeName, id, loId, moduleId } = this.$route.query;
              if (routeName === 'learnerCentralLo' && id && loId) {
                const idValid = isDigits(id);
                const loIdValid = isDigits(loId);
                const moduleIdValid = !moduleId || isDigits(moduleId);
                if (idValid && loIdValid && moduleIdValid) {
                  this.$router.replace({
                    name: "learnerCentralLo",
                    params: { id, loId, ...(moduleId && { moduleId }) }
                  });
                } else {
                  this.$router.replace({ name: 'active' });
                }
              } else if (routeName === 'learnerCentralLo' && isDigits(id)) {
                this.$router.replace({
                  name: 'learnerCentralLo',
                  params: { id }
                });
              } else if (routeName) {
                this.$router.replace({ name: routeName });
              } else {
                this.$router.replace({ name: 'active' });
              }
            })
            .catch((err) => {
              if (err.response && err.response.status === 403) {
                this.ssoUser = true;
              } else {
                this.invalidCred = true;
              }
              this.loading = false;
            });
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.login-btn {
  max-width: 200px;
}
</style>